import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createInvoice } from '@/lib/api';
import type { Invoice } from '@/types';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { FileText } from 'lucide-react';

interface CreditNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentInvoice: Invoice;
  onSuccess: () => void;
}

export function CreditNoteDialog({
  open,
  onOpenChange,
  parentInvoice,
  onSuccess,
}: CreditNoteDialogProps) {
  const queryClient = useQueryClient();

  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState<string>(
    parentInvoice.totalAmount.toString(),
  );
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setReason('');
      setAmount(parentInvoice.totalAmount.toString());
      setError(null);
    }
  }, [open, parentInvoice.totalAmount]);

  const mutation = useMutation({
    mutationFn: () =>
      createInvoice({
        companyId: parentInvoice.companyId,
        isCreditNote: true,
        parentInvoiceId: parentInvoice.id,
        lineItems: [
          {
            description: reason.trim(),
            quantity: 1,
            unitPrice: parseFloat(amount),
          },
        ],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['invoice', parentInvoice.id],
      });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onOpenChange(false);
      onSuccess();
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to create credit note');
    },
  });

  const parsedAmount = parseFloat(amount);
  const isValidAmount =
    !isNaN(parsedAmount) &&
    parsedAmount > 0 &&
    parsedAmount <= parentInvoice.totalAmount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-400" />
            Create Credit Note
          </DialogTitle>
          <DialogDescription>
            Issue a credit note against invoice{' '}
            {parentInvoice.invoiceNumber} (
            {formatCurrency(parentInvoice.totalAmount)})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="credit-reason">Reason *</Label>
            <Textarea
              id="credit-reason"
              placeholder="Enter the reason for this credit note..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="credit-amount">Amount *</Label>
            <Input
              id="credit-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={parentInvoice.totalAmount}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
            {!isNaN(parsedAmount) && parsedAmount > parentInvoice.totalAmount && (
              <p className="text-sm text-red-400">
                Amount cannot exceed invoice total (
                {formatCurrency(parentInvoice.totalAmount)})
              </p>
            )}
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={
              !reason.trim() || !isValidAmount || mutation.isPending
            }
          >
            {mutation.isPending ? 'Creating...' : 'Create Credit Note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
